import React, { useState } from 'react';
import { toast } from '@cotera/client/app/components';
import {
  ConnectedStatusCard,
  InlineDeleteConfirmation,
} from './credential-connection-cards';
import { useDeleteProviderAccount } from './hooks';

/**
 * Minimal shape shared by both callers' account lists (tool settings uses a
 * loosened `status: string`; the webhook side passes `ProviderAccountListItem`,
 * which is assignable to this).
 */
export type ProviderAccountCardItem = {
  id: string;
  displayName: string;
  status: string;
  createdAt: string;
};

/**
 * Renders a provider's connected accounts as delete-able cards, each with the
 * shared inline "type DELETE to confirm" flow. Deletion goes through the same
 * `useDeleteProviderAccount` method (and server-side uninstall hooks) as the
 * tool settings modal.
 *
 * Shared by the tool settings provider detail and the webhook provider
 * connection status so both surfaces look and behave identically.
 */
export const ProviderAccountCards: React.FC<{
  provider: string;
  accounts: readonly ProviderAccountCardItem[];
}> = ({ provider, accounts }) => {
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const deleteMutation = useDeleteProviderAccount(provider);

  const resolvedId =
    accounts.find((account) => account.status === 'default')?.id ??
    accounts.find((account) => account.status === 'active')?.id;

  return (
    <>
      {accounts.map((account) => (
        <div key={account.id} className="flex flex-col gap-2">
          <ConnectedStatusCard
            title={account.displayName}
            description={`Connected ${new Date(
              account.createdAt
            ).toLocaleDateString()}`}
            variant="default"
            isActive={account.id === resolvedId}
            onDelete={() =>
              setDeleteTarget(deleteTarget === account.id ? null : account.id)
            }
          />
          {deleteTarget === account.id && (
            <InlineDeleteConfirmation
              isPending={deleteMutation.isPending}
              onConfirm={() => {
                deleteMutation.mutate(account.id, {
                  onSuccess: () => {
                    setDeleteTarget(null);
                    toast.success('Credentials deleted');
                  },
                  onError: () => {
                    toast.error('Failed to delete credentials');
                  },
                });
              }}
              onCancel={() => setDeleteTarget(null)}
            />
          )}
        </div>
      ))}
    </>
  );
};
